import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, Mail, Lock, User, UserCheck, Tag } from 'lucide-react';
import { AuthService } from '../services/authService';

const LoginScreen = ({ onAuthSuccess, authError, isLoading }) => {
  const [mode, setMode] = useState('signin'); // 'signin', 'signup', 'reset'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    division: '',
    role: 'readwrite',
    analystCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analystCodeError, setAnalystCodeError] = useState('');
  const [isCheckingAnalystCode, setIsCheckingAnalystCode] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
    if (field === 'analystCode') {
      setAnalystCodeError('');
    }
  };

  // Helper function to generate initials from full name
  const generateInitials = (fullName) => {
    if (!fullName) return '';
    const names = fullName.trim().split(' ');
    const initials = names.map(name => name.charAt(0).toUpperCase()).join('');
    return initials.slice(0, 2); // Take first 2 initials
  };

  // Check analyst code uniqueness
  const checkAnalystCodeUniqueness = useCallback(async (code) => {
    if (!code) {
      setAnalystCodeError('');
      return true;
    }
    
    setIsCheckingAnalystCode(true);
    try {
      const exists = await AuthService.checkAnalystCodeExists(code);
      if (exists) {
        setAnalystCodeError('This employee code is already taken. Please choose a different one.');
        return false;
      } else {
        setAnalystCodeError('');
        return true;
      }
    } catch (error) {
      console.error('Error checking analyst code:', error);
      setAnalystCodeError('');
      return true; // Allow signup on error, DB constraint will catch duplicates
    } finally {
      setIsCheckingAnalystCode(false);
    }
  }, []);

  // Auto-generate analyst code when name changes
  useEffect(() => {
    if (formData.fullName) {
      const generatedCode = generateInitials(formData.fullName);
      // Only auto-set if user hasn't manually changed it
      if (!formData.analystCode || formData.analystCode === generateInitials(formData.fullName.split(' ').slice(0, -1).join(' '))) {
        setFormData(prev => ({ ...prev, analystCode: generatedCode }));
      }
    }
  }, [formData.fullName]);

  const validateForm = () => {
    if (!formData.email) {
      setError('Email is required');
      return false;
    }

    if (mode === 'signup') {
      // Email domain validation for signup
      if (!formData.email.endsWith('@clearlinecap.com')) {
        setError('Only @clearlinecap.com email addresses are allowed');
        return false;
      }
    }

    if (mode === 'signin' || mode === 'signup') {
      if (!formData.password) {
        setError('Password is required');
        return false;
      }

      if (mode === 'signup') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          return false;
        }

        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }

        if (!formData.fullName) {
          setError('Full name is required');
          return false;
        }

        if (!formData.division) {
          setError('Division is required');
          return false;
        }

        // Validate employee code for all users
        if (!formData.analystCode) {
          setError('Employee code is required');
          return false;
        }
        if (analystCodeError) {
          setError(analystCodeError.replace(/analyst code/gi, 'employee code'));
          return false;
        }
      }
    }

    return true;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      const { user, session } = await AuthService.signIn(formData.email, formData.password);
      
      if (user && session) {
        console.log('✅ Sign in successful:', user);
        await onAuthSuccess(user, session);
      } else {
        setError('Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('❌ Sign in error:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Use the user-provided analyst code (required for all users)
      const analystCode = formData.analystCode.toUpperCase();

      // Final check for analyst code uniqueness before signup
      const exists = await AuthService.checkAnalystCodeExists(analystCode);
      if (exists) {
        setError('This employee code is already taken. Please choose a different one.');
        setAnalystCodeError('This employee code is already taken.');
        setIsSubmitting(false);
        return;
      }

      const { user, session } = await AuthService.signUp(
        formData.email,
        formData.password,
        {
          fullName: formData.fullName,
          division: formData.division,
          analystCode: analystCode,
          role: formData.role
        }
      );

      if (user) {
        if (session) {
          // User is immediately signed in
          console.log('✅ Sign up and sign in successful:', user);
          await onAuthSuccess(user, session);
        } else {
          // Email confirmation required
          setSuccess('Account created! Please check your email to verify your account.');
          setMode('signin');
        }
      }
    } catch (error) {
      console.error('❌ Sign up error:', error);
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await AuthService.resetPassword(formData.email);
      if (error) throw error;
      
      setSuccess('Password reset email sent! Check your inbox.');
      console.log('Password reset email sent successfully to:', formData.email);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      division: '',
      role: 'readwrite',
      analystCode: ''
    });
    setError('');
    setSuccess('');
    setAnalystCodeError('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img 
            src="/clearline-logo.jpg" 
            alt="ClearLine Logo" 
            className="h-12 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Clearline Flow
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Hedge Fund Workflow Management
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Mode Tabs */}
          <div className="flex mb-6 border-b border-gray-200">
            <button
              onClick={() => switchMode('signin')}
              className={`flex-1 py-2 px-1 text-center text-sm font-medium border-b-2 ${
                mode === 'signin'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchMode('signup')}
              className={`flex-1 py-2 px-1 text-center text-sm font-medium border-b-2 ${
                mode === 'signup'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">{success}</div>
            </div>
          )}

          {/* Error Message */}
          {(error || authError) && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error || authError}</div>
            </div>
          )}

          {/* Sign In Form */}
          {mode === 'signin' && (
            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => switchMode('reset')}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSubmitting || isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isSubmitting ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {mode === 'signup' && (
            <form onSubmit={handleSignUp} className="space-y-6">
              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <div>
                <label htmlFor="full-name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1 relative">
                  <input
                    id="full-name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your full name"
                  />
                  <User className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <div>
                <label htmlFor="division" className="block text-sm font-medium text-gray-700">
                  Division
                </label>
                <div className="mt-1 relative">
                  <select
                    id="division"
                    value={formData.division}
                    onChange={(e) => handleInputChange('division', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  >
                    <option value="">Select Division</option>
                    <option value="Investment">Investment</option>
                    <option value="Ops">Ops</option>
                    <option value="Admin">Admin</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Engineering">Engineering</option>
                  </select>
                  <UserCheck className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              {/* Employee Code field - required for all users */}
              <div>
                <label htmlFor="employee-code" className="block text-sm font-medium text-gray-700">
                  Employee Code
                </label>
                <div className="mt-1 relative">
                  <input
                    id="employee-code"
                    type="text"
                    required
                    value={formData.analystCode}
                    onChange={(e) => handleInputChange('analystCode', e.target.value.toUpperCase())}
                    onBlur={() => checkAnalystCodeUniqueness(formData.analystCode)}
                    className={`appearance-none block w-full px-3 py-2 pl-10 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      analystCodeError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Use 2 letter initials (e.g. JD, AB) unless already taken"
                    maxLength={4}
                  />
                  <Tag className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  {isCheckingAnalystCode && (
                    <span className="absolute right-3 top-2.5 text-gray-400 text-sm">Checking...</span>
                  )}
                </div>
                {analystCodeError && (
                  <p className="mt-1 text-sm text-red-600">{analystCodeError.replace(/analyst code/gi, 'employee code')}</p>
                )}
                {!analystCodeError && formData.analystCode && (
                  <p className="mt-1 text-sm text-gray-500">
                    Your employee code will be: {formData.analystCode.toUpperCase()}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Access Level
                </label>
                <div className="mt-1 relative">
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="readonly">Read Only</option>
                    <option value="readwrite">Read/Write</option>
                  </select>
                  <UserCheck className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Confirm your password"
                  />
                  <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSubmitting || isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Password Reset Form */}
          {mode === 'reset' && (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <input
                    id="reset-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your email"
                  />
                  <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSubmitting || isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                {isSubmitting ? 'Sending Reset Email...' : 'Send Reset Email'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Back to Sign In
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen; 